import axios from 'axios'

/**
 * Detalle de una cuenta cliente.
 *
 * El cliente dejó de ser una fila de `users` y vive en `client_accounts`, así
 * que `/user/get/{id}` responde 404 para todos ellos. Las pantallas de detalle
 * se alcanzan tanto desde el listado de clientes como desde el de personas, de
 * modo que se consulta primero el cliente y, si no existe, la persona.
 */
export const fetchAccountDetail = async (id) => (await fetchAccountDetailWithKind(id)).value

/**
 * Igual que `fetchAccountDetail`, indicando además qué se encontró:
 * `kind` es `'account'` (cuenta cliente) o `'user'` (persona). Algunas acciones
 * del detalle, como asociar comunidades, se dirigen a la cuenta o a la persona
 * según el caso, y el id por sí solo no lo dice.
 */
export const fetchAccountDetailWithKind = async (id) => {
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  }

  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/client-account/get/${id}`,
      { headers },
    )
    if (response.data?.value) return { kind: 'account', value: response.data.value }
  } catch (error) {
    if (error.response?.status !== 404) throw error
  }

  const response = await axios.get(`${import.meta.env.VITE_API_URL}/user/get/${id}`, { headers })
  return { kind: 'user', value: response.data?.value || null }
}

/**
 * Clasificación del cliente con los nombres de la base de datos
 * (`client_account_types`): Horizontal, Vertical, Comercio, Delegación, o
 * "Vertical y Horizontal" cuando tiene las dos primeras (decisión 2026-09-09).
 * El back la resuelve en `type_label`; `roles` es la clave de compatibilidad
 * que queda de cuando la clasificación vivía en `role_user`.
 */
export const accountTypeLabel = (account, fallback = '-') => {
  if (!account) return fallback
  if (account.type_label) return account.type_label

  const types = Array.isArray(account.types) ? account.types.map((type) => type.type) : []
  if (types.includes('horizontal') && types.includes('vertical')) return 'Vertical y Horizontal'

  const labels = { horizontal: 'Horizontal', vertical: 'Vertical', comercio: 'Comercio', delegacion: 'Delegación' }
  const named = types.map((type) => labels[type]).filter(Boolean)
  return named.length ? named.join(', ') : fallback
}

/**
 * Tipos de la cuenta tal como vienen del back: `types` (relación) o `roles`
 * (clave de compatibilidad con el mismo contenido).
 */
export const accountTypes = (account) => {
  const source = Array.isArray(account?.types) ? account.types : (Array.isArray(account?.roles) ? account.roles : [])
  return source.map((type) => type?.type || type?.name).filter(Boolean)
}

/** Cuenta horizontal y vertical a la vez. Se mira en los tipos, nunca en la etiqueta. */
export const accountHasBothTypes = (account) => {
  const types = accountTypes(account)
  return types.includes('horizontal') && types.includes('vertical')
}
